import {
  ISkinScanQualityResult,
  ISkinScanQualityIssue,
  SkinScanQualityIssueEnum,
} from '@torim-app/types';

/**
 * Client-side, real-time image-quality checks for the Skin Scan camera.
 *
 * Runs entirely in the browser (free, instant) to guide the operator while framing:
 * brightness, sharpness (blur), and — where the experimental `FaceDetector` API is
 * available — face presence, size and centering. This is advisory only; the backend
 * runs an authoritative Gemini quality gate before any analysis is billed/stored.
 */

// Tunables (kept lenient to avoid nagging; the server gate is the source of truth).
const MIN_BRIGHTNESS = 55; // below => too dark
const MAX_BRIGHTNESS = 215; // above => too bright / blown out
const MIN_SHARPNESS = 6; // below => likely blurry (on the downscaled grayscale gradient scale)
const MIN_FACE_RATIO = 0.05; // face box area / frame area — below => too far
const MAX_FACE_RATIO = 0.62; // above => too close / cropped
const MAX_CENTER_OFFSET = 0.26; // normalized distance of face center from frame center

const SAMPLE_W = 160;
const SAMPLE_H = 120;

type DetectableSource = HTMLVideoElement | HTMLImageElement | HTMLCanvasElement;

let faceDetector: any = null;
let faceDetectorChecked = false;

function getFaceDetector(): any {
  if (faceDetectorChecked) return faceDetector;
  faceDetectorChecked = true;
  try {
    const FD = (window as any).FaceDetector;
    if (FD) faceDetector = new FD({ fastMode: true, maxDetectedFaces: 5 });
  } catch {
    faceDetector = null;
  }
  return faceDetector;
}

export function useImageQuality() {
  const canvas = typeof document !== 'undefined' ? document.createElement('canvas') : null;
  if (canvas) {
    canvas.width = SAMPLE_W;
    canvas.height = SAMPLE_H;
  }
  const ctx = canvas ? canvas.getContext('2d', { willReadFrequently: true }) : null;

  const faceDetectionSupported = !!getFaceDetector();

  /** Average luminance (0–255) and a relative sharpness score from a downscaled frame. */
  function measureExposureAndSharpness(source: DetectableSource): { brightness: number; sharpness: number } | null {
    if (!ctx || !canvas) return null;
    try {
      ctx.drawImage(source, 0, 0, SAMPLE_W, SAMPLE_H);
    } catch {
      return null; // e.g. source not ready yet
    }
    const { data } = ctx.getImageData(0, 0, SAMPLE_W, SAMPLE_H);

    // Grayscale luma buffer
    const gray = new Float32Array(SAMPLE_W * SAMPLE_H);
    let sum = 0;
    for (let i = 0, p = 0; i < data.length; i += 4, p++) {
      const luma = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
      gray[p] = luma;
      sum += luma;
    }
    const brightness = sum / (SAMPLE_W * SAMPLE_H);

    // Sharpness ≈ mean absolute Laplacian (edge energy). Blurry frames have low edge energy.
    let edgeSum = 0;
    let count = 0;
    for (let y = 1; y < SAMPLE_H - 1; y++) {
      for (let x = 1; x < SAMPLE_W - 1; x++) {
        const idx = y * SAMPLE_W + x;
        const lap =
          4 * gray[idx] - gray[idx - 1] - gray[idx + 1] - gray[idx - SAMPLE_W] - gray[idx + SAMPLE_W];
        edgeSum += Math.abs(lap);
        count++;
      }
    }
    const sharpness = count ? edgeSum / count : 0;
    return { brightness, sharpness };
  }

  async function detectFace(source: DetectableSource): Promise<{ ratio: number; centerOffset: number; count: number } | null> {
    const detector = getFaceDetector();
    if (!detector) return null;

    const w = (source as HTMLVideoElement).videoWidth || (source as HTMLImageElement).naturalWidth || (source as HTMLCanvasElement).width;
    const h = (source as HTMLVideoElement).videoHeight || (source as HTMLImageElement).naturalHeight || (source as HTMLCanvasElement).height;
    if (!w || !h) return null;

    try {
      const faces = await detector.detect(source);
      if (!faces?.length) return { ratio: 0, centerOffset: 1, count: 0 };
      // Use the largest detected face.
      const box = faces
        .map((f: any) => f.boundingBox)
        .sort((a: any, b: any) => b.width * b.height - a.width * a.height)[0];
      const ratio = (box.width * box.height) / (w * h);
      const cx = box.x + box.width / 2;
      const cy = box.y + box.height / 2;
      const offset = Math.hypot(cx / w - 0.5, cy / h - 0.5);
      return { ratio, centerOffset: offset, count: faces.length };
    } catch {
      return null;
    }
  }

  /**
   * Analyse a single frame and return Hebrew guidance. Safe to call repeatedly
   * (e.g. on a 500–700ms interval) against a live <video> element.
   */
  async function analyzeSource(source: DetectableSource): Promise<ISkinScanQualityResult> {
    const issues: ISkinScanQualityIssue[] = [];

    const exposure = measureExposureAndSharpness(source);
    if (exposure) {
      if (exposure.brightness < MIN_BRIGHTNESS)
        issues.push({ type: SkinScanQualityIssueEnum.TooDark, message: 'התאורה חלשה מדי — הוסיפו אור או התקרבו לחלון' });
      else if (exposure.brightness > MAX_BRIGHTNESS)
        issues.push({ type: SkinScanQualityIssueEnum.TooBright, message: 'יותר מדי אור — הימנעו מאור ישיר חזק' });

      if (exposure.sharpness < MIN_SHARPNESS)
        issues.push({ type: SkinScanQualityIssueEnum.Blurry, message: 'התמונה מטושטשת — החזיקו יציב לרגע' });
    }

    const face = await detectFace(source);
    if (face) {
      if (face.count === 0)
        issues.push({ type: SkinScanQualityIssueEnum.NoFace, message: 'לא זוהו פנים — מקמו את הפנים במסגרת' });
      else if (face.count > 1)
        issues.push({ type: SkinScanQualityIssueEnum.MultipleFaces, message: 'זוהו כמה פנים — שמרו פנים אחת בלבד במסגרת' });
      else {
        if (face.ratio < MIN_FACE_RATIO)
          issues.push({ type: SkinScanQualityIssueEnum.FaceTooFar, message: 'התקרבו מעט — הפנים רחוקות מדי' });
        else if (face.ratio > MAX_FACE_RATIO)
          issues.push({ type: SkinScanQualityIssueEnum.FaceTooClose, message: 'התרחקו מעט — הפנים קרובות מדי' });
        if (face.centerOffset > MAX_CENTER_OFFSET)
          issues.push({ type: SkinScanQualityIssueEnum.FaceNotCentered, message: 'מרכזו את הפנים במסגרת' });
      }
    }

    const usable = issues.length === 0;
    return {
      usable,
      issues,
      guidance: usable ? 'מצוין! אפשר לצלם' : issues[0].message,
    };
  }

  return {
    faceDetectionSupported,
    analyzeSource,
  };
}
